import { circle } from '@turf/turf';
import { flatten, isEqual } from 'lodash';
import { Types } from 'mongoose';
import { MultiFlightEntityType } from 'sonata-static-storage/lib/common/common.types';
import { getCartographyLevels } from 'sonata-static-storage/lib/entities';
import { worldCountries } from 'sonata-static-storage/lib/samples/worldCountries.samples';
import { Venue } from 'sonata-static-storage/lib/schemas/venue/venue.schema';

import { EMPTY_SAVE_STEP_COUNTS } from '@/types/saveSteps.types';
import { ATTRIBUTION_RADIUS_IN_METERS } from './constants';

import type { GeoJsonProperties } from 'geojson';
import type { AdminBoundariesChangeItem } from 'sonata-platform-components';
import type { AttributionSettings } from 'sonata-static-storage/lib/schemas/campaign/attributionSettings.schema';
import type { UserInterface } from 'sonata-static-storage/lib/schemas/user/user.schema';
import type { SaveStepCounts } from '@/types/saveSteps.types';
import type { ModifiedScreen, ModifiedVenue, ModifiedZone } from './components/MapView/map.types';
import type { AttributionType, AttributionZones, ZoneFeature } from './multiFlightMap.types';

export const generateRandomColor = (pastel: boolean) => {
  const generateColor = {
    componentToHex: (c: number) => {
      const hex = c.toString(16);
      return hex.length === 1 ? `0${hex}` : hex;
    },
    rgbToHex: (r: number, g: number, b: number) => {
      return `#${generateColor.componentToHex(r)}${generateColor.componentToHex(g)}${generateColor.componentToHex(b)}`;
    }
  };

  let r;
  let g;
  let b;
  if (!pastel) {
    r = Math.floor(Math.random() * 55);
    g = Math.floor(Math.random() * 55);
    b = Math.floor(Math.random() * 55);
  } else {
    r = Math.round(Math.random() * 127) + 127;
    g = Math.round(Math.random() * 127) + 127;
    b = Math.round(Math.random() * 127) + 127;
  }

  return generateColor.rgbToHex(r, g, b);
};

/** Default attribution settings: drive-to-store + visits ON, traffic OFF, no providers. */
export const buildEnabledAttributionSettings = (current?: AttributionSettings): AttributionSettings => ({
  ...current,
  driveToStore: { enabled: true },
  enabled: true,
  providers: [],
  settings: { traffic: false, visits: true }
});

export const buildEnabledFootfallAttributionSettings = (current?: AttributionSettings): AttributionSettings => ({
  ...current,
  driveToStore: { enabled: false },
  enabled: true,
  providers: [],
  settings: { traffic: false, visits: true }
});

export const getCommittedAttributionType = (venues: Partial<Venue>[] | undefined): AttributionType | undefined => {
  const liveVenues = (venues ?? []).filter(venue => !venue.deleted);
  if (liveVenues.length === 0) {
    return undefined;
  }
  return liveVenues.some(venue => Boolean(venue.geoDataId)) ? 'driveToStore' : 'footfall';
};

export const resolveAttributionMode = (
  activeSubsection: string | null | undefined,
  venues: Partial<Venue>[] | undefined
): AttributionType => {
  if (activeSubsection === 'proximityPoints') {
    return 'footfall';
  }
  if (activeSubsection === 'attributionPOIs') {
    return 'driveToStore';
  }
  return getCommittedAttributionType(venues) ?? 'driveToStore';
};

export const needsAttributionSettingsUpdate = (
  settings: AttributionSettings | undefined,
  mode: AttributionType
): boolean => !settings?.enabled || Boolean(settings.driveToStore?.enabled) !== (mode === 'driveToStore');

/** `insertionOrders` for IOs, `campaigns` for LIs — the venue relation array holding this entity's id. */
export const getVenueEntityRelationKey = (entityType: MultiFlightEntityType): 'campaigns' | 'insertionOrders' =>
  entityType === 'lineItem' ? 'campaigns' : 'insertionOrders';

/**
 * Splits the items a save step is about to persist into created/updated/removed,
 * from the same flags the save batches use: anything `isNew` goes to the create
 * batch, anything else only travels if it `isModified`, and `isRemoved` tells
 * the two apart. An item added and taken away again in the same session is
 * dropped by both batches, so it lands in no bucket.
 */
export const countSaveActions = <T extends { deleted?: boolean; isModified?: boolean }>(
  items: T[],
  {
    isNew,
    isRemoved = item => Boolean(item.deleted)
  }: { isNew: (item: T) => boolean; isRemoved?: (item: T) => boolean }
): SaveStepCounts =>
  items.reduce((acc, item) => {
    if (isNew(item)) {
      return item.deleted ? acc : { ...acc, created: acc.created + 1 };
    }
    if (!item.isModified && !isRemoved(item)) {
      return acc;
    }
    return isRemoved(item) ? { ...acc, removed: acc.removed + 1 } : { ...acc, updated: acc.updated + 1 };
  }, structuredClone(EMPTY_SAVE_STEP_COUNTS));

/** Merges the counts of two item collections that share a single save step. */
export const sumSaveStepCounts = (a: SaveStepCounts, b: SaveStepCounts): SaveStepCounts => ({
  created: a.created + b.created,
  removed: a.removed + b.removed,
  updated: a.updated + b.updated
});

/**
 * Venues losing their association with this entity, driven by the relation-array
 * diff (was this entity's id in the venue's `insertionOrders`/`campaigns` when
 * it was fetched, and is it gone now?) rather than by the local `deleted` flag,
 * which is session-local UI bookkeeping. Both the save itself and the save
 * progress counters read this, so they cannot disagree on what gets unlinked.
 */
export const getVenueIdsToRemove = (
  temporaryVenues: Partial<Venue>[],
  initialVenues: Partial<Venue>[] | undefined,
  entityType: MultiFlightEntityType,
  entityId: string
): string[] => {
  const relationKey = getVenueEntityRelationKey(entityType);
  return temporaryVenues.reduce((acc: string[], venue) => {
    const initialVenue = venue._id && initialVenues?.find(v => v._id?.toString() === venue._id?.toString());
    if (!initialVenue) {
      return acc;
    }
    const wasAssociated = ((initialVenue[relationKey] as string[] | undefined) ?? []).includes(entityId);
    const isAssociated = ((venue[relationKey] as string[] | undefined) ?? []).includes(entityId);
    return wasAssociated && !isAssociated ? [...acc, venue._id!.toString()] : acc;
  }, []);
};

/**
 * Finds a venue already present in `temporaryVenues` matching `candidate`, so a
 * removed-then-re-added venue can be restored in place instead of duplicated.
 * Sourced items (poiBrands/osm/poi/attribution) match by `geoDataId` +
 * `geoDataSourceId`; `myVenues` items (no geoData) match by name, coordinates,
 * address and group.
 */
export const findMatchingTemporaryVenue = (
  candidate: Partial<Venue>,
  temporaryVenues: Partial<Venue>[]
): Partial<Venue> | undefined => {
  if (candidate.geoDataId && candidate.geoDataSourceId) {
    return temporaryVenues.find(
      v => v.geoDataId === candidate.geoDataId && v.geoDataSourceId === candidate.geoDataSourceId
    );
  }
  return temporaryVenues.find(
    v =>
      v.name === candidate.name &&
      v.latlng?.lat === candidate.latlng?.lat &&
      v.latlng?.lng === candidate.latlng?.lng &&
      v.address === candidate.address &&
      isEqual(v.groups ?? [], candidate.groups ?? [])
  );
};

/**
 * Keeps one venue per sourced POI (`geoDataId` + `geoDataSourceId`). Several rows of an uploaded file can resolve
 * to the same POI, and each of them would otherwise be listed and added as a separate venue.
 * Venues that cannot be identified as the same POI (no geoData) are always kept.
 */
export const uniqueVenuesByGeoData = <T extends Partial<Venue>>(venues: T[]): T[] => {
  const seen = new Set<string>();
  return venues.filter(venue => {
    if (!venue.geoDataId || !venue.geoDataSourceId) {
      return true;
    }
    const key = `${venue.geoDataSourceId}:${venue.geoDataId}`;
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
};

/** Re-associates a previously removed venue with the current entity, in place. */
export const restoreVenueAssociation = (
  venue: Partial<Venue>,
  entityId: string,
  entityType: MultiFlightEntityType
): ModifiedVenue => {
  const relationKey = getVenueEntityRelationKey(entityType);
  const relations = (venue[relationKey] as string[] | undefined) ?? [];
  return {
    ...venue,
    [relationKey]: relations.includes(entityId) ? relations : [...relations, entityId],
    deleted: false,
    isModified: true
  };
};

export const getAttributionZonesObject = (zones: Partial<ModifiedZone>[]) =>
  zones
    .filter(({ type }) => type === 'attributionTorus')
    .reduce((acc, zone) => {
      if (zone._id) {
        acc[zone._id] = zone;

        return acc;
      }
      return acc;
    }, {} as AttributionZones);

export function createTorusRingFeature(
  center: [number, number],
  outerRadius: number,
  innerRadius: number,
  properties: GeoJsonProperties
): ZoneFeature | null {
  if (!outerRadius || !innerRadius) {
    return null;
  }
  const outer = circle(center, outerRadius, {
    steps: 64,
    units: 'meters'
  });
  const inner = circle(center, innerRadius, {
    steps: 64,
    units: 'meters'
  });
  if (!outer.geometry || !inner.geometry) {
    return null;
  }
  return {
    geometry: {
      coordinates: [outer.geometry.coordinates[0], inner.geometry.coordinates[0]],
      type: 'Polygon'
    },
    properties,
    type: 'Feature'
  };
}

export function getCountryLabel(countryCode: string, defaultValue = 'N/A'): string {
  return worldCountries.find(country => country.cca3 === countryCode.toUpperCase())?.name.common || defaultValue;
}

export const ZONE_COLOR = '#65A990';
export const ZONE_FILL_COLOR = 'rgba(194, 245, 212, 0.70)';

export function getZoneColor(uiColors: Record<string, string>, zone: Partial<ModifiedZone>) {
  const visibleZoneColor = zone.smart ? uiColors.smartZoneColor : ZONE_COLOR;

  return zone.visible ? visibleZoneColor : uiColors.unselectedZoneColor;
}

export function getZoneFillColor(uiColors: Record<string, string>, zone: Partial<ModifiedZone>) {
  const visibleZoneFillColor = zone.smart ? uiColors.smartZoneColor : ZONE_FILL_COLOR;

  return zone.visible ? visibleZoneFillColor : uiColors.unselectedZoneColor;
}

export function getZoneFillOpacity(zone: Partial<ModifiedZone>, defaultOpacity: number) {
  return zone.visible && !zone.smart ? 1 : defaultOpacity;
}

export function getVenueAttribution(
  venueId: string,
  attributionZones: AttributionZones
): { active: boolean; attributionMode?: AttributionType; enabled: boolean } {
  const venueAttributionZones = Object.values(attributionZones).filter(zone => zone.shop === venueId);
  if (venueAttributionZones.length === 0) {
    return { active: false, enabled: false };
  }

  const undeletedTorusConfig = venueAttributionZones[0]?.torusConfig?.find(config => !config.deleted);
  const enabled = true;
  const active = Boolean(undeletedTorusConfig) && venueAttributionZones.some(zone => zone.visible);

  return { active, attributionMode: venueAttributionZones[0]?.attributionMode, enabled };
}

export type VenueAttributionState = 'enabled' | 'disabled' | 'proximity';

/**
 * One-pass lookup of each venue's render state so venues can be painted from a
 * GeoJSON feature property instead of calling getVenueAttribution per venue per
 * render (O(zones) each). Same semantics as getVenueAttribution: a footfall
 * zone renders the proximity thumbtack; otherwise a venue with a visible zone
 * that has a live torus config renders enabled, everything else disabled.
 */
export function buildVenueAttributionLookup(attributionZones: AttributionZones): Record<string, VenueAttributionState> {
  const byVenue: Record<string, { anyVisible: boolean; firstZone?: Partial<ModifiedZone> }> = {};

  Object.values(attributionZones).forEach(zone => {
    if (!zone.shop) {
      return;
    }
    const venueId = String(zone.shop);
    const previous = byVenue[venueId];
    byVenue[venueId] = {
      anyVisible: (previous?.anyVisible ?? false) || Boolean(zone.visible),
      firstZone: previous?.firstZone ?? zone
    };
  });

  return Object.entries(byVenue).reduce(
    (lookup, [venueId, { anyVisible, firstZone }]) => {
      const hasLiveConfig = Boolean(firstZone?.torusConfig?.some(config => !config.deleted));
      lookup[venueId] =
        firstZone?.attributionMode === 'footfall' ? 'proximity' : anyVisible && hasLiveConfig ? 'enabled' : 'disabled';
      return lookup;
    },
    {} as Record<string, VenueAttributionState>
  );
}

type FormatDateOptions = {
  withSeconds?: boolean;
  withTime?: boolean;
};

export function getFormattedDateByUser(date: Date, user: UserInterface, opts?: FormatDateOptions): string {
  const { withSeconds = false, withTime = false } = opts || {};
  const getFormattedDate = (locale: string): string => {
    const formatter = new Intl.DateTimeFormat(locale, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      ...(withTime && {
        hour: '2-digit',
        hour12: false,
        minute: '2-digit'
      }),
      ...(withSeconds && {
        second: '2-digit'
      })
    });
    return formatter.format(date);
  };
  try {
    const userCountry = user?.country ?? 'us';
    const formattedDate = getFormattedDate(userCountry);
    return formattedDate;
  } catch (_e) {
    return getFormattedDate('us');
  }
}

function calculateAreaFromBoundingBox(bbox: [number, number, number, number]): number {
  const [minLon, minLat, maxLon, maxLat] = bbox;
  const EARTH_RADIUS_KM = 6371;

  const latDiff = (maxLat - minLat) * (Math.PI / 180);
  const lonDiff = (maxLon - minLon) * (Math.PI / 180);

  const lat1 = minLat * (Math.PI / 180);
  const lat2 = maxLat * (Math.PI / 180);

  const latDistance = EARTH_RADIUS_KM * latDiff;
  const avgLat = (lat1 + lat2) / 2;
  const lonDistance = EARTH_RADIUS_KM * Math.cos(avgLat) * lonDiff;

  return Math.abs(latDistance * lonDistance);
}

export async function generateZonesFromAdminBoundaryItems(
  items: AdminBoundariesChangeItem[]
): Promise<Partial<ModifiedZone>[]> {
  const zonePromises: Promise<Partial<ModifiedZone>[]>[] = items.map(async item => {
    const shapesBoundingBoxes = await getCartographyLevels(item.country, item.shape, {
      fields: ['boundingBox', 'name'],
      filters: { [item.shape]: item.list.map((i: any) => i.name) }
    });
    const boundingBoxesMap = shapesBoundingBoxes.reduce(
      (acc, shape) => {
        if (shape.boundingBox) {
          acc[shape.name] = shape.boundingBox;
        }
        return acc;
      },
      {} as Record<string, [number, number, number, number]>
    );
    return item.list.map((listItem: any) => {
      const level = Object.entries(listItem.tags).find(([, values]) =>
        (values as any).includes(listItem.name)
      )?.[0] as string;
      const cartographyLevelId = listItem.name;
      const boundingBox = boundingBoxesMap[cartographyLevelId];

      return {
        _id: new Types.ObjectId().toString(),
        ...(item.area || boundingBox
          ? { area: item.area ? item.area : calculateAreaFromBoundingBox(boundingBox) }
          : {}),
        boundingBox,
        cartographyLevel: level,
        cartographyLevelId,
        country: item.country,
        isModified: true,
        latlng: {
          lat: 0,
          lng: 0
        },
        name: listItem.description,
        type: 'geojson',
        version: 2,
        visible: true
      } as Partial<ModifiedZone>;
    });
  });

  return flatten(await Promise.all(zonePromises));
}

export function getAttributionBasedLocation(
  venue?: Partial<Venue>,
  entityType?: MultiFlightEntityType,
  entityId?: string,
  attributionMode?: AttributionType,
  radiusInMeters?: number
): Partial<ModifiedZone> {
  const radius = radiusInMeters ?? ATTRIBUTION_RADIUS_IN_METERS;
  return {
    attributionMode,
    autoSmart: false,
    color: '#990000',
    country: venue?.country,
    customName: false,
    deleted: false,
    groups: [],
    latlng: venue?.latlng,
    mooh: {
      totemIds: []
    },
    name: `${venue?.name} Attribution Location`,
    retargetingCandidate: false,
    shop: venue!._id!,
    smart: false,
    sonataLI: false,
    sourceIndexes: [],
    torusConfig: [
      {
        area: 0,
        deleted: false,
        geoHashes: [],
        id: new Types.ObjectId().toString(),
        name: `${venue?.name} - ${radius}m`,
        radius,
        visible: true
      }
    ],
    type: 'attributionTorus',
    visible: true,
    ...(entityType === 'lineItem' && { campaign: entityId }),
    ...(entityType === 'insertionOrder' && { insertionOrder: entityId })
  };
}

const isMappableScreen = (screen: Partial<ModifiedScreen>) =>
  !screen.deleted &&
  typeof screen.latitude === 'number' &&
  typeof screen.longitude === 'number' &&
  screen.source_element_id !== undefined &&
  screen.source_element_id !== null;

// Screens with exactly the same coordinates (e.g. several screens in a mall) share a key
// and are drawn as one stack marker on the map.
const getScreenStackKey = (screen: Partial<ModifiedScreen>) => `${screen.longitude},${screen.latitude}`;

export const groupScreensByCoordinates = (screens: Partial<ModifiedScreen>[]) => {
  const stacks = new Map<string, Partial<ModifiedScreen>[]>();
  for (const screen of screens) {
    if (!isMappableScreen(screen)) {
      continue;
    }
    const key = getScreenStackKey(screen);
    const stack = stacks.get(key);
    if (stack) {
      stack.push(screen);
    } else {
      stacks.set(key, [screen]);
    }
  }
  return stacks;
};
